import { CREATE_FEATURE_FLAG, GET_FEATURE_FLAGS, GET_FEATURE_FLAG_LOGS, UPDATE_FEATURE_FLAG } from '@/gql/featureFlags';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { fetchData } from '@/services/graphql';
import { FeatureFlag, FeatureFlagCreatePayload } from '@/types/featureFlags';
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

type APIError = Error;

// Function to format feature flag names consistently
export const formatFeatureFlagName = (name: string): string => {
  // Convert to lowercase and replace spaces with underscores
  return name.toLowerCase().replace(/\s+/g, '_');
};

export const featureFlagKeys = {
  all: ['featureFlags'] as const,
  lists: () => [...featureFlagKeys.all, 'list'] as const,
  list: (filters: string) => [...featureFlagKeys.lists(), { filters }] as const,
  details: () => [...featureFlagKeys.all, 'detail'] as const,
  detail: (id: string) => [...featureFlagKeys.details(), id] as const,
  logs: () => [...featureFlagKeys.all, 'logs'] as const,
};

interface GetFeatureFlagsResponse {
  featureFlags: Array<{
    id: string;
    name: string;
    description: string;
    isEnabled: boolean;
    modifiedAt: string;
    createdAt: string;
    createdById: string;
    updatedById: string;
  }>;
}

interface CreateFeatureFlagResponse {
  insertFeatureFlagsOne: {
    id: string;
    name: string;
    description: string;
    isEnabled: boolean;
    modifiedAt: string;
    createdAt: string;
  };
}

interface UpdateFeatureFlagResponse {
  updateFeatureFlagsByPk: {
    id: string;
    name: string;
    description: string;
    isEnabled: boolean;
    modifiedAt: string;
    createdAt: string;
    createdById: string;
    updatedById: string;
  };
}

interface UpdateFeatureFlagData {
  name?: string;
  description?: string;
  isEnabled?: boolean;
}

export interface UpdateFeatureFlagVariables {
  id: string;
  data: UpdateFeatureFlagData;
}

interface ToggleFeatureFlagVariables {
  id: string;
  isEnabled: boolean;
}

interface UpdateFeatureFlagDescriptionVariables {
  id: string;
  description: string;
}

interface FeatureFlagLogUser {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
}

interface FeatureFlagLog {
  id: string;
  featureFlagId: string;
  oldData: string | null;
  newData: string | null;
  action: string;
  oldName: string | null;
  newName: string | null;
  oldDescription: string | null;
  newDescription: string | null;
  oldIsEnabled: boolean | null;
  newIsEnabled: boolean | null;
  changed_by: FeatureFlagLogUser;
  changedById: string;
  createdAt: string;
  modifiedAt: string;
}

interface GetFeatureFlagLogsResponse {
  featureFlagLogs: FeatureFlagLog[];
}

export const useGetFeatureFlags = (options?: UseQueryOptions<FeatureFlag[], APIError>) => {
  return useQuery<FeatureFlag[], APIError>({
    queryKey: featureFlagKeys.lists(),
    queryFn: async (): Promise<FeatureFlag[]> => {
      try {
        const { featureFlags } = await fetchData<GetFeatureFlagsResponse>(GET_FEATURE_FLAGS);

        return featureFlags.map((flag) => ({
          id: flag.id,
          name: flag.name,
          description: flag.description,
          isEnabled: flag.isEnabled,
          created_at: flag.createdAt,
          updatedAt: flag.modifiedAt,
          createdById: flag.createdById,
          updatedById: flag.updatedById,
        }));
      } catch (error) {
        console.error('Error fetching feature flags:', error);
        throw error instanceof Error ? error : new Error('Failed to fetch feature flags');
      }
    },
    ...options,
  });
};

export const useUpdateFeatureFlag = (
  options?: UseMutationOptions<FeatureFlag, APIError, UpdateFeatureFlagVariables>,
) => {
  const queryClient = useQueryClient();

  return useMutation<FeatureFlag, APIError, UpdateFeatureFlagVariables>({
    mutationFn: async ({ id, data }: UpdateFeatureFlagVariables): Promise<FeatureFlag> => {
      if (!id || typeof id !== 'string') {
        throw new Error('Invalid ID: Must provide a valid UUID');
      }

      const updateData = { ...data };
      if (updateData.name) {
        updateData.name = formatFeatureFlagName(updateData.name);
      }

      const { updateFeatureFlagsByPk } = await fetchData<UpdateFeatureFlagResponse>(UPDATE_FEATURE_FLAG, {
        id,
        _set: updateData,
      });

      return {
        id: updateFeatureFlagsByPk.id,
        name: updateFeatureFlagsByPk.name,
        description: updateFeatureFlagsByPk.description,
        isEnabled: updateFeatureFlagsByPk.isEnabled,
        created_at: updateFeatureFlagsByPk.createdAt,
        updatedAt: updateFeatureFlagsByPk.modifiedAt,
        createdById: updateFeatureFlagsByPk.createdById,
        updatedById: updateFeatureFlagsByPk.updatedById,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.lists() });
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.logs() });
    },
    ...options,
  });
};

export const useCreateFeatureFlag = (options?: UseMutationOptions<FeatureFlag, APIError, FeatureFlagCreatePayload>) => {
  const queryClient = useQueryClient();

  return useMutation<FeatureFlag, APIError, FeatureFlagCreatePayload>({
    mutationFn: async (payload: FeatureFlagCreatePayload): Promise<FeatureFlag> => {
      const id = uuidv4();
      const formattedName = formatFeatureFlagName(payload.name);

      const { insertFeatureFlagsOne } = await fetchData<CreateFeatureFlagResponse>(CREATE_FEATURE_FLAG, {
        object: {
          id,
          name: formattedName,
          description: payload.description,
          isEnabled: false,
        },
      });

      return {
        id: insertFeatureFlagsOne.id,
        name: insertFeatureFlagsOne.name,
        description: insertFeatureFlagsOne.description,
        isEnabled: insertFeatureFlagsOne.isEnabled,
        created_at: insertFeatureFlagsOne.createdAt,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.lists() });
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.logs() });
    },
    ...options,
  });
};

export const useToggleFeatureFlag = (
  options?: UseMutationOptions<FeatureFlag, APIError, ToggleFeatureFlagVariables>,
) => {
  const updateFeatureFlag = useUpdateFeatureFlag();

  return useMutation<FeatureFlag, APIError, ToggleFeatureFlagVariables>({
    mutationFn: async ({ id, isEnabled }: ToggleFeatureFlagVariables): Promise<FeatureFlag> => {
      if (!id || typeof id !== 'string') {
        throw new Error('Invalid ID: Must provide a valid UUID');
      }

      return updateFeatureFlag.mutateAsync({
        id,
        data: { isEnabled },
      });
    },
    onSuccess: () => {
      // The logs will be automatically invalidated by useUpdateFeatureFlag
    },
    ...options,
  });
};

export const useUpdateFeatureFlagDescription = (
  options?: UseMutationOptions<FeatureFlag, APIError, UpdateFeatureFlagDescriptionVariables>,
) => {
  const updateFeatureFlag = useUpdateFeatureFlag();

  return useMutation<FeatureFlag, APIError, UpdateFeatureFlagDescriptionVariables>({
    mutationFn: async ({ id, description }: UpdateFeatureFlagDescriptionVariables): Promise<FeatureFlag> => {
      if (!id || typeof id !== 'string') {
        throw new Error('Invalid ID: Must provide a valid UUID');
      }

      return updateFeatureFlag.mutateAsync({
        id,
        data: { description },
      });
    },
    onSuccess: () => {
      // The logs will be automatically invalidated by useUpdateFeatureFlag
    },
    ...options,
  });
};

export const useGetFeatureFlagLogs = (options?: UseQueryOptions<FeatureFlagLog[], APIError>) => {
  return useQuery<FeatureFlagLog[], APIError>({
    queryKey: featureFlagKeys.logs(),
    queryFn: async (): Promise<FeatureFlagLog[]> => {
      try {
        const { featureFlagLogs } = await fetchData<GetFeatureFlagLogsResponse>(GET_FEATURE_FLAG_LOGS);
        return featureFlagLogs;
      } catch (error) {
        console.error('Error fetching feature flag logs:', error);
        throw error instanceof Error ? error : new Error('Failed to fetch feature flag logs');
      }
    },
    ...options,
  });
};

export { useFeatureFlag };
